/**
 * Reading a business off its own website (03 §business context).
 *
 * The old client had a fourteen-step pipeline over a profile and it taught three things worth keeping, all of them
 * about what a person watching it sees ([08](../../../docs/08-prior-art.md)):
 *
 * 1. **One list is the order.** Reordering the list reorders the pipeline; nothing else decides what runs next.
 * 2. **Split the long work so the progress moves.** Its brand pass ran four model calls behind one static line for
 *    a minute; splitting it into visible steps was the fix. Ours is visible steps for the same reason.
 * 3. **Say what each step found, in one line.** "Found 8 photos" under a ticked step is worth more than a bar.
 *
 * What it does not keep is its shape (ADR 0029). It had one flow for a business with a website and another for one
 * without, patched apart, and choice screens that could leave somebody stuck. This is one run for every business: a
 * step with nothing to read skips itself, and the one choice — which pictures, which logo, which colours — is asked
 * once at the end, beside the conversation, and never stops anything else.
 */

export interface AnalysisStepSpec {
  key: string;
  /** What the customer sees while it runs. Present tense, plain, no jargon. */
  label: string;
  /** Progress once this step has finished. Strictly increasing, so the bar never goes backwards. */
  pct: number;
  /** True when the step stops and waits for the customer to choose something. */
  gate: boolean;
}

/**
 * How a step turned out.
 *
 * Three of these and not two, because "there was nothing to do" and "I tried and found nothing" are different
 * things to the person watching, and neither is a failure. A plumber with no website and no listing should not see
 * five red lines; he should see that we looked, that there was nothing to look at, and one question.
 */
export type StepOutcome = "ok" | "none" | "skipped";

/** The order. Reordering this reorders the pipeline; nothing else decides what comes next. */
export const ANALYSIS_STEPS: readonly AnalysisStepSpec[] = [
  { key: "place", label: "Finding you on Google", pct: 15, gate: false },
  { key: "site", label: "Reading your website", pct: 35, gate: false },
  { key: "rivals", label: "Seeing who else is near you", pct: 50, gate: false },
  { key: "words", label: "Working out what you do", pct: 70, gate: false },
  { key: "pictures", label: "Gathering your pictures, logo and colours", pct: 95, gate: false },
  { key: "choose", label: "Choosing what to keep", pct: 100, gate: true },
];

export type AnalysisStatus = "idle" | "running" | "waiting" | "done" | "failed";

/** What one step found, in the words the customer reads under it. */
export interface StepFinding {
  outcome: StepOutcome;
  detail: string;
  at: string;
}

export interface AnalysisState {
  status: AnalysisStatus;
  /** The step running now, or the gate waiting for an answer. Null when nothing is happening. */
  step: string | null;
  progress: number;
  findings: Record<string, StepFinding>;
  error?: string;
  startedAt?: string;
  /**
   * The Google listing that came closest without matching the name or the town, by its place id — the one thing about
   * it Google's terms allow keeping. The guide asks whether it is the business; a yes starts a lookup of it (ADR 0029).
   */
  nearMiss?: { placeId: string };
  /** The listing the owner said is theirs, which this run reads by id instead of searching. */
  confirmedPlace?: string;
  /**
   * What the check screen offers to tap, from the same `read_site` call that wrote the page (ADR 0049): a few short
   * answers for the sections a lookup can only guess, and three voices to pick *How we sound* from. Kept for this run
   * and gone when the next one starts.
   */
  suggest?: Suggestions;
}

/** A way the business could sound: what to call it, one line saying what it is, and one post written in it. */
export interface Voice {
  name: string;
  describe: string;
  sample: string;
}

export interface Suggestions {
  what: string[];
  who: string[];
  works: string[];
  voices: Voice[];
}

export const ANALYSIS_IDLE: AnalysisState = { status: "idle", step: null, progress: 0, findings: {} };

export const analysisStep = (key: string | null): AnalysisStepSpec | undefined =>
  ANALYSIS_STEPS.find((s) => s.key === key);

/** The step after this one, or null when the pipeline is over. */
export function analysisNext(key: string | null): string | null {
  const i = ANALYSIS_STEPS.findIndex((s) => s.key === key);
  if (i < 0) return ANALYSIS_STEPS[0]?.key ?? null;
  return ANALYSIS_STEPS[i + 1]?.key ?? null;
}

/**
 * The state after a step has run.
 *
 * A step that finds nothing does not fail the analysis: a business with no website still gets everything the map
 * knew, and a customer watching this cares about what was found, not about which of our calls returned. Only a
 * step that says so explicitly stops the run.
 *
 * A gate is asked only after a step that found something to choose from. Pictures nobody found are not a question, so
 * the run is simply over.
 */
export function analysisAfter(state: AnalysisState, key: string, finding: StepFinding, opts: { fatal?: boolean } = {}): AnalysisState {
  const spec = analysisStep(key);
  const next = analysisNext(key);
  const findings = { ...state.findings, [key]: finding };
  const progress = Math.max(state.progress, spec?.pct ?? state.progress);
  if (opts.fatal) return { ...state, status: "failed", step: key, progress, findings, error: finding.detail };
  if (!next) return { ...state, status: "done", step: null, progress: 100, findings };
  if (analysisStep(next)?.gate) {
    return finding.outcome === "ok"
      ? { ...state, status: "waiting", step: next, progress, findings }
      : { ...state, status: "done", step: null, progress: 100, findings };
  }
  return { ...state, status: "running", step: next, progress, findings };
}

/** The state once the owner has answered the gate the run is waiting at. */
export function analysisChosen(state: AnalysisState, detail: string, at: string): AnalysisState {
  const key = state.step ?? "choose";
  return { ...state, status: "done", step: null, progress: 100, findings: { ...state.findings, [key]: { outcome: "ok", detail, at } } };
}
